import { useEffect, useRef, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../lib/store';
import {
  useUploadDatasplittingJSONFileMutation,
  useUploadDatasplittingJSONS3PathMutation,
} from '../../api/createProjectApiSlice';
import { DATA_IMPORT_MODES } from '../../constants';
import {
  clearUploadedDatasplittingJSONFileName,
  setUploadedDatasplittingJSONFileName,
  setDatasplitImportMode,
  setDatasplitJsonS3FilePath,
  clearDatasplitJsonS3FilePath,
} from '../../state/createProjectSlice';
import {
  selectUploadedDatasplittingJSONFileName,
  selectWorkflowId,
  selectDatasplitJsonS3FilePath,
  selectDatasplitImportMode,
} from '../../state/selector';

interface UseDataSplittingProps {
  setRequiredFieldsFilled: (fieldsFilled: boolean) => void;
}

interface UseDataSplittingResults {
  uploadedDatasplittingJSONFileName: string | null;
  handleRemoveFile: () => void;
  handleClick: () => void;
  handleDrop: (event: React.DragEvent<HTMLDivElement>) => void;
  isUploadDatasplittingJSONFileLoading: boolean;
  inputRef: React.RefObject<HTMLInputElement>;
  handleUploadDatasplittingJSONFile: (file: File | null) => Promise<void>;
  uploadError: string | null;
  datasplitImportMode: string;
  setDatasplitMode: (mode: (typeof DATA_IMPORT_MODES)[number]) => void;

  uploadedDatasplitS3FilePath: string | null;
  datasplitS3Input: string;
  setDatasplitS3Input: (value: string) => void;

  handleSetDatasplitS3Path: (s3Path: string) => Promise<void>;
  handleRemoveDatasplitS3Path: () => void;

  datasplitS3Error: string | null;
  isSetDatasplitS3PathLoading: boolean;
}

export const useDataSplitting = ({
  setRequiredFieldsFilled,
}: UseDataSplittingProps): UseDataSplittingResults => {
  const workflowId = useAppSelector(selectWorkflowId);
  const [uploadDatasplittingJSONFile, { isLoading: isUploadDatasplittingJSONFileLoading }] =
    useUploadDatasplittingJSONFileMutation();
  const uploadedDatasplittingJSONFileName = useAppSelector(selectUploadedDatasplittingJSONFileName);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const datasplitImportMode = useAppSelector(selectDatasplitImportMode);
  const uploadedDatasplitS3FilePath = useAppSelector(selectDatasplitJsonS3FilePath);

  const [setDatasplitS3Path, { isLoading: isSetDatasplitS3PathLoading }] =
    useUploadDatasplittingJSONS3PathMutation();

  const [datasplitS3Input, setDatasplitS3Input] = useState('');
  const [datasplitS3Error, setDatasplitS3Error] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement | null>(null);

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (datasplitImportMode === DATA_IMPORT_MODES[0]) {
      setRequiredFieldsFilled(!!uploadedDatasplittingJSONFileName);
    }

    if (datasplitImportMode === DATA_IMPORT_MODES[1]) {
      setRequiredFieldsFilled(!!uploadedDatasplitS3FilePath);
    }
  }, [
    datasplitImportMode,
    uploadedDatasplittingJSONFileName,
    uploadedDatasplitS3FilePath,
    setRequiredFieldsFilled,
  ]);

  useEffect(() => {
    setDatasplitS3Input(uploadedDatasplitS3FilePath || '');
  }, [uploadedDatasplitS3FilePath]);

  const handleUploadDatasplittingJSONFile = async (file: File | null) => {
    if (!file) return;
    if (!workflowId) return;

    try {
      await uploadDatasplittingJSONFile({ file, workflowId }).unwrap();
      dispatch(setUploadedDatasplittingJSONFileName(file.name));
      setRequiredFieldsFilled(true);
      setUploadError(null);
    } catch (error) {
      console.error('Failed to upload JSON file', error);
      setUploadError(
        'Failed to upload JSON file : ' + (error as { data: { message: string } }).data.message
      );
    }
  };

  const handleClick = () => {
    inputRef.current?.click();
    setUploadError(null);
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];

    if (!file) return;

    try {
      await handleUploadDatasplittingJSONFile(file);
      setUploadError(null);
    } catch (error) {
      setUploadError(
        'Failed to upload JSON file : ' + (error as { data: { message: string } }).data.message
      );
      console.error('Failed to upload JSON file', error);
    }
  };

  const handleSetDatasplitS3Path = async (s3Path: string) => {
    if (!s3Path || !workflowId) return;

    try {
      await setDatasplitS3Path({ s3Path, workflowId }).unwrap();
      dispatch(setDatasplitJsonS3FilePath(s3Path));
      setDatasplitS3Error(null);
    } catch (error) {
      setDatasplitS3Error(
        'Failed to set S3 path : ' + (error as { data: { message: string } }).data.message
      );
    }
  };

  const handleRemoveFile = () => {
    dispatch(clearUploadedDatasplittingJSONFileName());
    setRequiredFieldsFilled(false);

    if (inputRef.current) {
      inputRef.current.value = '';
    }
    setUploadError(null);
  };

  const handleRemoveDatasplitS3Path = () => {
    dispatch(clearDatasplitJsonS3FilePath());
    setRequiredFieldsFilled(false);
    setDatasplitS3Error(null);
  };

  const setDatasplitMode = (mode: (typeof DATA_IMPORT_MODES)[number]) => {
    dispatch(setDatasplitImportMode(mode));
  };

  return {
    uploadedDatasplittingJSONFileName,
    handleRemoveFile,
    handleClick,
    handleDrop,
    isUploadDatasplittingJSONFileLoading,
    inputRef,
    handleUploadDatasplittingJSONFile,
    uploadError,

    datasplitImportMode,
    setDatasplitMode,

    uploadedDatasplitS3FilePath,
    datasplitS3Input,
    setDatasplitS3Input,

    handleSetDatasplitS3Path,
    handleRemoveDatasplitS3Path,

    datasplitS3Error,
    isSetDatasplitS3PathLoading,
  };
};
